import React from 'react';
import { X, Heart, Diamond, Star, Lock, Check, Clock, Info } from 'lucide-react';
import { AngelSkin } from './AngelSkinsShop';
interface SkinPreviewModalProps {
  skin: AngelSkin;
  onClose: () => void;
  onUnlock: () => void;
}
export const SkinPreviewModal: React.FC<SkinPreviewModalProps> = ({
  skin,
  onClose,
  onUnlock
}) => {
  // Helper function to render the rarity badge
  const renderRarityBadge = (rarity: string) => {
    const colors = {
      Common: 'bg-gray-500',
      Rare: 'bg-blue-500',
      Epic: 'bg-purple-500',
      Legendary: 'bg-gradient-to-r from-yellow-500 to-amber-600'
    };
    const colorClass = colors[rarity as keyof typeof colors] || colors.Common;
    return <div className={`${colorClass} text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center`} data-id="element-19591">
        <Star size={12} className="mr-1" fill="currentColor" data-id="element-19592" />
        {rarity}
      </div>;
  };
  // Helper function to render currency icon
  const renderCurrencyIcon = (type: string) => {
    return type === 'Love Tokens' ? <Heart size={16} className="text-pink-500" fill="currentColor" data-id="element-19593" /> : <Diamond size={16} className="text-blue-400" data-id="element-19594" />;
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-19595">
      <div className="relative w-full max-w-4xl bg-white dark:bg-gray-900 rounded-xl overflow-hidden shadow-2xl animate-in zoom-in-95 duration-300" data-id="element-19596">
        <button className="absolute right-4 top-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors" onClick={onClose} data-id="element-19597">
          <X size={18} data-id="element-19598" />
        </button>
        <div className="flex flex-col md:flex-row" data-id="element-19599">
          {/* Image section */}
          <div className="relative w-full md:w-1/2 aspect-square md:aspect-auto" data-id="element-19600">
            <img src={skin.preview_image_url} alt={skin.skin_name} className={`w-full h-full object-cover ${skin.status === 'Locked' ? 'grayscale opacity-70' : ''}`} data-id="element-19601" />
            {/* Locked overlay */}
            {skin.status === 'Locked' && <div className="absolute inset-0 bg-black/50 flex items-center justify-center" data-id="element-19602">
                <Lock size={48} className="text-white opacity-80" data-id="element-19603" />
              </div>}
            {/* FOMO label */}
            {skin.fomo_label && <div className="absolute top-4 right-4 bg-gradient-to-r from-pink-600 to-purple-600 text-white text-sm px-3 py-1 rounded-full z-10" data-id="element-19604">
                {skin.fomo_label}
              </div>}
            {/* Countdown timer */}
            {skin.time_limited && skin.countdown && <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur-sm text-white px-3 py-1.5 rounded-lg flex items-center" data-id="element-19605">
                <Clock size={16} className="mr-2" data-id="element-19606" />
                {skin.countdown}
              </div>}
            {/* Angel indicator */}
            <div className="absolute top-4 left-4 bg-gray-900/80 backdrop-blur-sm text-white text-sm px-3 py-1 rounded-full z-10 flex items-center" data-id="element-19607">
              <span className="mr-1" data-id="element-19608">👼</span>
              {skin.angel_name}
            </div>
            {/* Active indicator */}
            {skin.status === 'Active' && <div className="absolute bottom-4 left-4 bg-green-500 text-white text-sm px-3 py-1 rounded-full z-10 flex items-center" data-id="element-19609">
                <Check size={14} className="mr-1" data-id="element-19610" />
                Active
              </div>}
          </div>
          {/* Info section */}
          <div className="w-full md:w-1/2 p-6 flex flex-col" data-id="element-19611">
            <div className="flex items-center justify-between mb-3" data-id="element-19612">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-19613">
                {skin.skin_name}
              </h2>
              {renderRarityBadge(skin.rarity)}
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6" data-id="element-19614">
              {skin.short_description}
            </p>
            <div className="space-y-6 mb-6" data-id="element-19615">
              <div data-id="element-19616">
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2" data-id="element-19617">
                  VISUAL THEME
                </h3>
                <p className="text-gray-700 dark:text-gray-300" data-id="element-19618">
                  {skin.visual_theme}
                </p>
              </div>
              <div data-id="element-19619">
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2" data-id="element-19620">
                  PERSONALITY
                </h3>
                <p className="text-gray-700 dark:text-gray-300" data-id="element-19621">
                  {skin.personality_tone}
                </p>
              </div>
              <div data-id="element-19622">
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2" data-id="element-19623">
                  UNLOCK METHOD
                </h3>
                <p className="text-gray-700 dark:text-gray-300 flex items-center" data-id="element-19624">
                  <Info size={16} className="mr-2 text-gray-500 dark:text-gray-400" data-id="element-19625" />
                  {skin.unlock_method}
                </p>
              </div>
            </div>
            <div className="mt-auto" data-id="element-19626">
              <div className="flex items-center justify-between mb-4" data-id="element-19627">
                <div className="flex items-center space-x-2" data-id="element-19628">
                  {renderCurrencyIcon(skin.currency_type)}
                  <span className="font-bold text-xl text-gray-900 dark:text-white" data-id="element-19629">
                    {skin.price}
                  </span>
                  <span className="text-gray-500 dark:text-gray-400" data-id="element-19630">
                    {skin.currency_type}
                  </span>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm ${skin.status === 'Active' ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' : skin.status === 'Unlocked' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'}`} data-id="element-19631">
                  {skin.status}
                </div>
              </div>
              {skin.status === 'Locked' ? <button className="w-full py-3 bg-gradient-to-r from-pink-600 to-purple-600 rounded-lg text-white font-medium hover:shadow-lg hover:shadow-pink-500/20 transition-all" onClick={onUnlock} data-id="element-19632">
                  Unlock Now
                </button> : skin.status === 'Unlocked' ? <button className="w-full py-3 bg-gradient-to-r from-green-600 to-teal-600 rounded-lg text-white font-medium hover:shadow-lg hover:shadow-green-500/20 transition-all" onClick={onUnlock} data-id="element-19633">
                  Activate Skin
                </button> : <button className="w-full py-3 bg-gray-200 dark:bg-gray-700 rounded-lg text-gray-500 dark:text-gray-400 font-medium cursor-not-allowed" disabled data-id="element-19634">
                  Currently Active
                </button>}
            </div>
          </div>
        </div>
      </div>
    </div>;
};